/* App shell: header, routing, deck state, persistence.
 *
 * No framework — hyperscript and ES modules. Vite serves this in development
 * and builds it for deployment (`npm start`, `npm run build`); it has to be
 * served either way, since ES modules and the meta.json fetch don't work from
 * a file:// URL.
 */

import { h, fill, toast, $, debounce } from './ui/dom.js';
import { loadCards, isLoaded, getCards } from './data/cards.js';
import { loadMeta } from './data/meta.js';
/* `saveDecks`/`loadDecks` are gone from here: which decks exist and where they
 * live is `data/deckstore.js`'s question now, and it answers it for both
 * backends. deck.js still owns the localStorage half underneath. */
import {
  emptyDeck, totalCards, validate, encodeDeck, decodeDeck,
  reconcile, DECK_SIZE,
} from './data/deck.js';
import { createBuilder } from './ui/builder.js';
import { createBoard } from './ui/board.js';
import { createMeta } from './ui/meta.js';
import { createDeckView } from './ui/deckview.js';
import { createAuthDialog } from './ui/auth.js';
import { createDecksDialog } from './ui/decks.js';
import {
  openActive, createDeck, updateDeck, migrateLocalDeck, setActiveDeckId,
} from './data/deckstore.js';
import { initAuth, onAuthChange, isSignedIn, userLabel, signOut } from './data/auth.js';
import { installErrorBuffer, initTelemetry, report } from './telemetry.js';
import { initAnalytics, MASK_ATTR } from './analytics.js';

/* Tabs. Deck View is deliberately not one: it is a destination you arrive at
 * with a specific deck (a share link, and later a permalink), not a place you
 * browse to with nothing in mind. */
const VIEWS = [
  ['board', 'Playtest'],
  ['builder', 'Deck Builder'],
  ['meta', 'Meta'],
];

const IS_TAB = new Set(VIEWS.map(([k]) => k));

const app = {
  deck: emptyDeck(),
  /* Which stored deck `app.deck` is a working copy of. Null means "not saved
   * yet" — a deck you started but have not touched, which must not cost a row
   * until it holds something. */
  deckId: null,
  view: 'builder',
  go, deckChanged, persist, renderChrome, openDeck, adoptDeck,
};

let views = {};
let authDialog = null;
let decksDialog = null;
const els = {};

/* ---------------------------------------------------------------------- */
/* chrome                                                                  */
/* ---------------------------------------------------------------------- */

const PANEL_ID = 'view-panel';

/**
 * One tab button, built the same way in both places that need one.
 *
 * `buildChrome` and `renderChrome` used to construct these independently with
 * identical props, so the ARIA had to be kept in sync by hand — and wasn't.
 * The tablist declared `role="tablist"` and the buttons `role="tab"`, but the
 * panel had no `role`, no `id` and no `aria-labelledby`, and the tabs had no
 * `aria-controls`. A screen reader announced tabs it could not connect to
 * anything.
 */
function tabButton(k, label) {
  const selected = app.view === k;
  /* Roving tabindex: only the active tab is in the tab order, which is what
   * the tablist pattern expects. On a non-tab view (Deck View) nothing is
   * selected, so the first tab holds the entry point — otherwise every tab
   * would be tabindex=-1 and the whole tablist would drop out of the keyboard
   * order with no way back. */
  const inTabOrder = selected || (!IS_TAB.has(app.view) && k === VIEWS[0][0]);
  return h('button', {
    id: `tab-${k}`,
    role: 'tab',
    'aria-selected': String(selected),
    'aria-controls': PANEL_ID,
    tabindex: inTabOrder ? '0' : '-1',
    onclick: () => go(k),
  }, label);
}

function buildChrome() {
  els.tabs = h('nav.tabs', { role: 'tablist' },
    VIEWS.map(([k, label]) => tabButton(k, label)));

  /* A button, not a div: it is the way into My Decks, and the thing it names
   * — which deck you are editing — is exactly what that dialog changes. */
  els.deckChip = h('button.deckchip', {
    type: 'button',
    'aria-haspopup': 'dialog',
    title: 'Switch deck, rename, duplicate or delete',
    onclick: () => decksDialog?.open(),
  });
  els.status = h('div', { style: { font: '500 10px/1 var(--mono)', color: 'var(--tx-5)' } });
  els.account = h('div.account');

  els.header = h('header.appbar',
    h('div.brand',
      h('div.brand-mark', 'CT'),
      h('div.brand-name', 'CARD THEORY'),
      h('div.brand-tag', 'OPTCG'),
    ),
    els.tabs,
    els.deckChip,
    h('div.spacer'),
    els.status,
    h('button.btn', { onclick: () => views.board?.toggleHelp() }, 'Rules ref'),
    h('button.btn', {
      onclick: () => {
        /* Share what is on screen. On Deck View that is someone else's list,
         * not your builder deck — copying a link to a deck you are not looking
         * at is a quietly wrong answer. */
        const subject = (app.view === 'deckview' && views.deckview?.deck) || app.deck;
        const url = `${location.origin}${location.pathname}#${encodeDeck(subject)}`;
        navigator.clipboard?.writeText(url)
          .then(() => toast('Share link copied — the whole deck is in the URL.', 'good'))
          .catch(() => toast('Could not access the clipboard.', 'bad'));
      },
    }, 'Share'),
    /* "New deck" used to clear the one you had, behind a confirm, because
     * there was only ever one slot. It now starts another and leaves yours
     * alone — which is what the words always meant. */
    h('button.btn', {
      onclick: async () => {
        const r = await createDeck({ ...emptyDeck(), name: 'New deck' });
        if (!r.ok) { toast(`Could not create a deck: ${r.error}`, 'bad'); return; }
        openDeck(r.deck.id, r.deck);
        decksDialog?.refresh();
      },
    }, 'New deck'),
    els.account,
  );

  els.main = h('div', {
    id: PANEL_ID,
    role: 'tabpanel',
    'aria-labelledby': `tab-${app.view}`,
    style: { flex: '1', display: 'flex', minHeight: '0' },
  });
  /* The legal pages have to be reachable from the app, not only from the
   * landing site — a privacy policy belongs where the data is collected. Real
   * links to real documents, so they work with JavaScript broken and are
   * followable by anything that reads the page.
   *
   * `flex: none` in a `100vh` column: the board and the builder take the rest,
   * and this must not compete with them for height. */
  els.foot = h('footer.appfoot',
    h('a', { href: '/privacy' }, 'Privacy'),
    h('a', { href: '/terms' }, 'Terms'),
    h('span.foot-note', 'Unofficial fan project — not affiliated with Bandai'),
  );

  return h('div#app', els.header, els.main, els.foot);
}

function renderChrome() {
  fill(els.tabs, VIEWS.map(([k, label]) => tabButton(k, label)));

  const n = totalCards(app.deck);
  const problems = validate(app.deck);
  const ok = problems.length === 0;

  fill(els.deckChip,
    h('div.dot', {
      style: { background: ok ? 'var(--ok)' : 'var(--danger)', boxShadow: `0 0 0 3px ${ok ? 'rgba(79,211,154,.16)' : 'rgba(224,87,79,.16)'}` },
    }),
    h('div.nm', app.deck.name || 'Untitled deck'),
    h('div.meta', `${n}/${DECK_SIZE} · ${ok ? 'legal' : (problems[0]?.msg ?? 'not legal')}`),
  );
}

/* ---------------------------------------------------------------------- */
/* account                                                                 */
/* ---------------------------------------------------------------------- */

/* Signed out is not a degraded state — it is the state the app spent its whole
 * life in, and every screen still works there. So this is one quiet button
 * until there is something to say. */
function renderAccount() {
  if (!els.account) return;

  if (!isSignedIn()) {
    fill(els.account, h('button.btn', {
      onclick: () => authDialog?.open('in'),
    }, 'Sign in'));
    return;
  }

  const label = userLabel();
  fill(els.account,
    /* This is an email address, and session replay records the DOM. A click
     * heatmap is one thing; a stranger's email sitting in a third party's
     * recording is another. See src/analytics.js. */
    h('span.accountname', { title: label, [MASK_ATTR]: 'true' }, label),
    h('button.btn', {
      onclick: async () => {
        const res = await signOut();
        /* Success is announced by the auth event, which is also what fires on a
         * sign-out that happened in another tab. Only the failure is ours. */
        if (!res.ok) toast(res.error, 'bad');
      },
    }, 'Sign out'),
  );
}

/**
 * Keep the header honest, and say something when the answer changes.
 *
 * Driven by the transition rather than the event, because Supabase emits on
 * restoring a stored session and on every silent token refresh. Toasting those
 * would greet you on every reload and interrupt you an hour into a game.
 */
function watchAuth(initialUser) {
  let lastUid = initialUser?.id ?? null;

  onAuthChange(user => {
    const uid = user?.id ?? null;
    renderAccount();
    if (uid === lastUid) return;
    const signedIn = !lastUid && uid;
    lastUid = uid;
    toast(uid ? `Signed in as ${userLabel()}.` : 'Signed out.', 'good');
    if (signedIn) adoptLocalDecks();
  });
}

/**
 * Offer the browser's decks into the account, once, on first sign-in.
 *
 * The store does the deciding — it copies rather than moves, and only into an
 * account with no decks — so this is safe to call on every sign-in and is a
 * no-op for everyone who already has decks saved. Failure is silent by design:
 * it is a convenience, and a toast about a migration nobody asked for is worse
 * than the migration not happening.
 */
let adopting = null;
function adoptLocalDecks() {
  /* Called from two places now — the sign-in transition and the end of boot —
   * and `migrateLocalDeck` is only idempotent *sequentially*: two concurrent
   * calls would each see an account with no decks and each copy everything in.
   * Sharing the in-flight promise makes the second a no-op without pinning the
   * result, so signing out and into a different account still migrates. */
  if (adopting) return adopting;
  adopting = runAdoption().finally(() => { adopting = null; });
  return adopting;
}

async function runAdoption() {
  const r = await migrateLocalDeck();
  if (!r.ok || !r.migrated) return;
  toast(`Saved ${r.migrated} deck${r.migrated === 1 ? '' : 's'} to your account.`, 'good');
  decksDialog?.refresh();

  /* And put a deck back on screen (#109).
   *
   * `boot()` ran `openActive()` before this auth event arrived, found an
   * account with no decks in it, and fell back to an empty one. Until now
   * nothing re-opened anything, so the header read "Untitled deck · 0/50"
   * while the deck sat safely in My Decks — indistinguishable, for that first
   * moment, from signing in having thrown your work away.
   *
   * Only when the open deck is not an account deck. Two cases, and both are
   * this same ordering:
   *
   *   no id at all   the empty fallback. Nothing to lose by replacing it.
   *   a `local:` id  signing in without a page load — the email flow, where
   *                  OAuth's redirect does not intervene. That id names an
   *                  index in a browser array and cannot be updated against
   *                  the account at all, so the next save would fail on a
   *                  malformed uuid. Re-pointing at the copy fixes a second
   *                  bug quietly.
   *
   * A deck already open from the account is left alone. */
  const onAccountDeck = app.deckId && !String(app.deckId).startsWith('local:');
  if (!onAccountDeck && r.opened) openDeck(r.opened.id, r.opened);
}

/* ---------------------------------------------------------------------- */
/* routing                                                                 */
/* ---------------------------------------------------------------------- */

function go(name) {
  if (!views[name]) return;
  app.view = name;
  fill(els.main, views[name].root);

  /* Keep the panel pointing at the tab that now labels it. A non-tab view has
   * no tab to point at, and a dangling aria-labelledby is worse than none —
   * it names an element that isn't there. */
  if (IS_TAB.has(name)) {
    els.main.setAttribute('aria-labelledby', `tab-${name}`);
    els.main.setAttribute('role', 'tabpanel');
  } else {
    els.main.removeAttribute('aria-labelledby');
    els.main.removeAttribute('role');
  }

  views[name].render();
  renderChrome();
}

/**
 * Show a deck that isn't yours.
 *
 * Nothing here touches `app.deck`. A shared list is something you look at; it
 * becomes your working deck only when you click Edit Deck.
 */
function showSharedDeck(d) {
  const dropped = reconcile(d);
  if (dropped.length) toast(`This deck has ${dropped.length} card id(s) we don't know.`, 'bad');
  views.deckview?.show(d);
  go('deckview');
}

function deckChanged() {
  persist();
  renderChrome();
  for (const v of Object.values(views)) v.onDeckChange?.();
  if (app.view && views[app.view]) views[app.view].render();
}

/**
 * Switch which stored deck the builder is editing.
 *
 * Takes a copy: `app.deck` is mutated in place by `addCard`/`removeCard`, and
 * handing it the object the deck list is still rendering from would make the
 * list follow every keystroke.
 */
function openDeck(id, deck) {
  app.deckId = id;
  app.deck = { ...emptyDeck(), ...deck };
  reconcile(app.deck);
  setActiveDeckId(id);
  renderChrome();
  for (const v of Object.values(views)) v.onDeckChange?.();
  if (app.view && views[app.view]) views[app.view].render();
}

/**
 * Take a deck that isn't yours — a share link's — as a new deck of your own.
 *
 * Adds rather than replaces. Under the single storage slot, "Edit Deck" could
 * only assign over `app.deck`, which is why #12 needed a confirm dialog and
 * why Deck View was split out at all. With a deck list there is nothing to
 * weigh up: yours stays where it is and the shared one joins it.
 *
 * Falls back to opening it unsaved if the store refuses, because being handed
 * the deck is the thing the user asked for and a failed write should not
 * swallow it.
 */
async function adoptDeck(deck) {
  const r = await createDeck(deck);
  if (r.ok) {
    openDeck(r.deck.id, r.deck);
    decksDialog?.refresh();
    return r.deck;
  }
  toast(`Opened, but not saved: ${r.error}`, 'bad');
  openDeck(null, deck);
  return null;
}

/* ---------------------------------------------------------------------- */
/* persistence                                                             */
/* ---------------------------------------------------------------------- */

/* Writing is debounced and never concurrent.
 *
 * Signed out this is a localStorage write and the cost is nothing, which is
 * why it was a bare call before. Signed in it is a network round trip, and
 * `deckChanged` fires on every single card add — so an unthrottled version
 * would put one request per click on the wire, out of order, with the last
 * response winning rather than the last edit.
 *
 * `dirty` is what makes the coalescing correct rather than merely quiet: an
 * edit that lands mid-flight sets it, and the in-flight save re-runs when it
 * finishes instead of being dropped. */
let saving = false;
let dirty = false;

async function flush() {
  if (saving) { dirty = true; return; }
  saving = true;
  try {
    do {
      dirty = false;
      const snapshot = { name: app.deck.name, leader: app.deck.leader, cards: { ...app.deck.cards } };

      /* An untouched deck does not get a row. Everyone who opens the app has
       * one, and creating it would fill an account with "Untitled deck". */
      if (!app.deckId) {
        if (!snapshot.leader && !Object.keys(snapshot.cards).length) return;
        const made = await createDeck(snapshot);
        if (!made.ok) { warnOnce(made.error); return; }
        app.deckId = made.deck.id;
        setActiveDeckId(made.deck.id);
        renderChrome();
        continue;
      }

      const saved = await updateDeck(app.deckId, snapshot);
      if (!saved.ok) { warnOnce(saved.error); return; }
    } while (dirty);
  } finally {
    saving = false;
  }
}

/* One complaint per problem, not one per keystroke. A deck that cannot be
 * saved keeps failing on every edit, and a toast per card is unusable. */
let lastWarning = null;
function warnOnce(msg) {
  if (msg === lastWarning) return;
  lastWarning = msg;
  toast(`Could not save: ${msg}`, 'bad');
}

const schedulePersist = debounce(() => { flush(); }, 400);

/* A declaration, not `const persist = debounce(...)`: the `app` object at the
 * top of this file references `persist` by name, and a const there would still
 * be in its temporal dead zone. */
function persist() { schedulePersist(); }

/* ---------------------------------------------------------------------- */
/* boot                                                                    */
/* ---------------------------------------------------------------------- */

/**
 * A read-only handle on what the running app loaded.
 *
 * The browser tests used to reach the card database with
 * `import('/src/data/cards.js')`, which is a dev-server path: the build has no
 * `/src`, so every spec that introspected the pool could only ever run against
 * `npm start`. That is the half of the e2e suite that could not be pointed at
 * the artefact which actually deploys (#69).
 *
 * Deliberately not gated behind a build flag. A flag would mean the artefact
 * under test is not the artefact that ships, which is the exact failure mode
 * running e2e against `dist/` exists to close. Nothing here is a new exposure:
 * these are pure accessors over a public card dump the page has already
 * fetched, and shipping a build you can inspect is the same call as
 * `sourcemap: true` in vite.config.mjs.
 */
function installDebugHandle() {
  window.deckLab = Object.freeze({
    cards: getCards,
    encodeDeck,
    decodeDeck,
  });
}

async function boot() {
  installDebugHandle();

  /* Before anything else that can throw. The SDK arrives asynchronously and
   * only if a DSN is configured; these two listeners are what make sure a
   * failure *during* boot — the class of bug nothing else here can see — is
   * still reported once it does. */
  installErrorBuffer();
  initTelemetry();
  /* Not awaited, and it answers a different question from the line above:
   * Sentry is what threw, Clarity is what they were doing. */
  initAnalytics();

  document.body.appendChild(buildChrome());
  fill(els.main, h('div.pane', h('div', {
    style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(126px,1fr))', gap: '11px' },
  }, Array.from({ length: 18 }, () => h('div.skeleton')))));
  els.status.textContent = 'loading card data…';

  authDialog = createAuthDialog();
  document.body.appendChild(authDialog.root);
  decksDialog = createDecksDialog(app);
  document.body.appendChild(decksDialog.root);
  renderAccount();

  /* Not awaited. Restoring a session normally means reading storage, but an
   * expired token makes it a network round trip — and the app must not wait on
   * the account service to show you a deck builder that never needed one. */
  initAuth().then(user => {
    watchAuth(user);
    renderAccount();
  });

  try {
    await loadCards((done, total) => {
      els.status.textContent = `loading cards ${done}/${total}…`;
    });
  } catch (err) {
    els.status.textContent = 'card data failed to load';
    fill(els.main, h('div.pane', h('div.empty-note',
      `Could not load the card database: ${err.message}. Check your connection and reload.`)));
    return;
  }

  await loadMeta();

  /* A share link no longer competes with local storage, because they are no
   * longer the same slot. The builder always restores your own deck; the link
   * opens read-only on top of it. */
  const shared = location.hash.length > 3 ? decodeDeck(location.hash) : null;

  /* Whichever deck you had open, from whichever backend is answering. Falls
   * back to the first in the list, then to an empty deck — a remembered id can
   * outlive the deck it named (deleted on another device, or a `local:` id
   * left behind by signing in). */
  const opened = await openActive();
  if (opened.ok && opened.deck) {
    app.deckId = opened.deck.id;
    app.deck = { ...emptyDeck(), name: opened.deck.name, leader: opened.deck.leader, cards: opened.deck.cards };
  }
  const dropped = reconcile(app.deck);
  if (dropped.length) toast(`Removed ${dropped.length} unknown card id(s).`, 'bad');

  /* Again here, and not only on the sign-in transition (#109).
   *
   * `watchAuth` fires the migration when the user id *changes*, which is the
   * right trigger and an unreliable one: whether the OAuth return produces a
   * transition depends on whether `getSession()` beats `detectSessionInUrl`.
   * Lose that race and the session is simply *there* at boot, no transition
   * ever happens, and the account keeps opening an empty deck on every reload
   * with the real one sitting unmigrated in local storage.
   *
   * Deliberately not awaited: it is a network round trip, and the builder has
   * never waited on the account service to show you a deck. */
  if (isSignedIn()) adoptLocalDecks();

  views = {
    builder: createBuilder(app),
    board: createBoard(app),
    meta: createMeta(app),
    deckview: createDeckView(app),
  };

  const cardCount = Object.keys(await loadCards()).length;
  els.status.textContent = `${cardCount.toLocaleString()} cards`;

  if (shared) showSharedDeck(shared);
  else go(totalCards(app.deck) ? 'builder' : 'meta');

  // Keyboard: quick view switching and search focus.
  window.addEventListener('keydown', e => {
    /* Ignore anything carrying a modifier. Ctrl+1 and Cmd+1 are the browser's
     * own tab shortcuts, and this handler was switching the Card Theory view at
     * the same time — so returning to the app via Cmd+1 silently moved you to
     * a different screen. */
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    // Not every event target is an Element; `matches` is missing on document.
    if (typeof e.target?.matches === 'function' && e.target.matches('input, textarea, select')) {
      if (e.key === 'Escape') e.target.blur();
      return;
    }
    if (e.key === '1') go('board');
    else if (e.key === '2') go('builder');
    else if (e.key === '3') go('meta');
    else if (e.key === '/') { e.preventDefault(); go('builder'); views.builder.focusSearch(); }
  });

  /* Following a link opens it read-only.
   *
   * There used to be a confirm() here, because this handler wrote straight
   * into app.deck and persist() saves exactly one deck — so opening a shared
   * link destroyed whatever you were building. The guard asked permission
   * before overwriting; routing to Deck View removes the reason to ask. */
  window.addEventListener('hashchange', () => {
    const d = decodeDeck(location.hash);
    if (!d) return;
    showSharedDeck(d);
  });
}

boot().catch(err => {
  console.error(err);
  /* The buffer catches what is *thrown*; a rejected boot() is caught here and
   * would otherwise never reach it. */
  report(err);
  document.body.appendChild(h('div.pane', h('div.empty-note', `Startup failed: ${err.message}`)));
});
